import type { FC } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Value, ChangeValue } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { percentageSignFilter } from "app/filters/ngFilters";
import { numFilter } from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import { InfoTooltip } from "app/components/React/Tooltip/InfoTooltip/InfoTooltip";
import { getChangeValue } from "app/pages/digital-marketing/backlinks/reports/overview/BacklinksOverviewDataHelpers";
import {
    BacklinksOverviewSingleMetricWidgetContainer,
    ChangeValueWrapper,
    Title,
    TitleRow,
    Tooltip,
    ValuesRow,
} from "app/pages/digital-marketing/backlinks/reports/overview/components/SingleMetricWidget/BacklinksSingleMetricWidgetStyledComponents";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

const valueChangeFilter: (a: number, b?: number, c?: string) => string = percentageSignFilter();

interface IBacklinksSingleMetricWidgetParams {
    title: string;
    tooltip: string;
    currentValue: number;
    previousValue: number;
    dataAutomationMap?: {
        container?: string;
        title?: string;
        value?: string;
        change?: string;
    };
}

export const BacklinksSingleMetricWidget: FC<IBacklinksSingleMetricWidgetParams> = (props) => {
    const translate = useTranslation();
    const { title, tooltip, currentValue, previousValue, dataAutomationMap } = props;

    const getChangeComponent = (incomingCurrentValue: number, incomingPreviousValue: number) => {
        if (incomingPreviousValue === undefined) {
            return null;
        }

        const change = getChangeValue(incomingPreviousValue, incomingCurrentValue);
        const value =
            change === 0 ? (
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[300]}
                    data-automation="value-no-change"
                >
                    {translate("apps.common.noChange")}
                </Text>
            ) : (
                valueChangeFilter(Math.abs(change), 2)
            );

        return (
            <ChangeValueWrapper data-automation={dataAutomationMap?.change}>
                <ChangeValue
                    value={value as string}
                    isDecrease={change < 0}
                    unsigned={change === 0}
                />
            </ChangeValueWrapper>
        );
    };

    return (
        <BacklinksOverviewSingleMetricWidgetContainer data-automation={dataAutomationMap?.container}>
            <TitleRow>
                <Title data-automation={dataAutomationMap?.title}>{title}</Title>
                <Tooltip>
                    <InfoTooltip infoText={tooltip} />
                </Tooltip>
            </TitleRow>
            <ValuesRow>
                <div data-automation={dataAutomationMap?.value}>
                    <Value
                        displayValue={numFilter(currentValue).toString()}
                        isSmallSize={true}
                    />
                </div>
                {getChangeComponent(currentValue, previousValue)}
            </ValuesRow>
        </BacklinksOverviewSingleMetricWidgetContainer>
    );
};
